import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX, faSearch, faCircleInfo, faUsers, faBuilding, faTag } from "@fortawesome/free-solid-svg-icons";
import useDebounce from "../hooks/useDebounce";
import usePartners from "../hooks/usePartners";
import api from "../services/api";
import "../components/componentsCss/ProceduresModal.css";

const ProceduresModal = ({ patient, onClose, onProceed }) => {
  const procedures = useMemo(() => {
    return Array.isArray(patient?.procedures)
      ? patient.procedures.sort((a, b) => a.id - b.id)
      : Array.isArray(patient?.pending)
        ? patient.pending.sort((a, b) => a.id - b.id)
        : [];
  }, [patient]);

  // Estados
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState("all");
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [payments, setPayments] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [insurances, setInsurances] = useState([]);
  const [aux, setAux] = useState([]);

  const [loading, setLoading] = useState(false);
  const [budgetedProcedures, setBudgetedProcedures] = useState([]);
  const [affiliateCompanies, setAffiliateCompanies] = useState({});

  // Hook da API
  const procedureIds = useMemo(() => {
    return [...new Set(selectedProcedures.map((p) => p.procedure_id))];
  }, [selectedProcedures]);
  const debouncedProcedureIds = useDebounce(procedureIds, 500);
  const { partners, loading: loadingDoctors } = usePartners(
    debouncedProcedureIds
  );

  // useEffect(() => {
  //   console.log("selectedProcedures: ", selectedProcedures);
  //   console.log("Patient: ", patient);
  // }, [patient, selectedProcedures]);

  //------------buscar agreement
  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        const response = await api.get("api/agreements");
        setAux(response.data);
        setInsurances(
          response.data?.data?.map((agreement) => agreement.name) ?? []
        );
      } catch (error) {
        console.error("Error fetching agreements:", error);
      }
    };
    fetchAgreements();
  }, []);

  const [availableDoctorsCount, setAvailableDoctorsCount] = useState(0);

  useEffect(() => {
    const availableDoctorsCount = Array.isArray(partners?.data?.suggestions)
      ? partners.data.suggestions.length
      : 0;
    setAvailableDoctorsCount(availableDoctorsCount);
  }, [partners]);

  // Filtros
  const sectors = [...new Set(procedures.map((p) => p.setor_name))];

  const filteredProcedures = procedures.filter((p) => {
    const matchesSearch =
      (p.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (p.cbhpm_code?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    const matchesSector =
      selectedSector === "all" || p.setor_name === selectedSector;
    return matchesSearch && matchesSector;
  });

  // Ações
  const updatePayment = (procId, newPayment) =>
    setPayments((prev) => ({ ...prev, [procId]: newPayment }));

  const applyPaymentToSector = (payment) => {
    if (selectedSector === "all") return;
    const target = procedures.filter((p) => p.setor_name === selectedSector);
    setPayments((prev) => {
      const next = { ...prev };
      target.forEach((p) => {
        next[p.id] = payment;
      });
      return next;
    });
  };

  const handleProcedureSelect = (proc) => {
    const exists = selectedProcedures.some((p) => p.id === proc.id);
    const next = exists
      ? selectedProcedures.filter((p) => p.id !== proc.id)
      : [...selectedProcedures, proc];

    setSelectedProcedures(next);

    const uniqueSectors = [...new Set(next.map((p) => p.setor_name))];
    const uniqueAffiliates = [...new Set(next.map((p) => p.affiliate_company_id))];

    setErrorMessage(
      (uniqueSectors.length > 1) || (uniqueAffiliates.length > 1)
        ? "Não é possível agendar procedimentos de setores/empresas diferentes"
        : ""
    );
  };

  const toggleSelectAll = () => {
    if (selectedSector === "all") return;
    const allSelected = filteredProcedures.every((p) =>
      selectedProcedures.some((sp) => sp.id === p.id)
    );
    setSelectedProcedures(
      allSelected
        ? selectedProcedures.filter(
          (p) => !filteredProcedures.some((fp) => fp.id === p.id)
        )
        : [
          ...selectedProcedures,
          ...filteredProcedures.filter(
            (fp) => !selectedProcedures.some((sp) => sp.id === fp.id)
          ),
        ]
    );
  };

  const handleProceed = () => {
    if (
      selectedProcedures.length === 0 ||
      errorMessage ||
      availableDoctorsCount === 0
    )
      return;

    const proceduresWithPayment = selectedProcedures.map((p) => ({
      ...p,
      payment: payments[p.id] ?? { type: "private", insurance: "" },
    }));
    onProceed(proceduresWithPayment);
  };

  useEffect(() => {
    if (selectedSector === "all") return;
    const filtered = selectedProcedures.filter(
      (p) => p.setor_name === selectedSector
    );

    if (filtered.length !== selectedProcedures.length) {
      setSelectedProcedures(filtered);
      setPayments((prev) => {
        const next = {};
        filtered.forEach((p) => {
          if (prev[p.id]) next[p.id] = prev[p.id];
        });
        return next;
      });
    }

    setErrorMessage("");
  }, [selectedSector]);

  useEffect(() => {
    const fetchAffiliateCompanies = async () => {
      setLoading(true); // Set loading state to true
      const proceduresWithAffiliateId = patient.pending.filter(proc => proc.affiliate_company_id);

      if (proceduresWithAffiliateId.length === 0) {
        setLoading(false); // Set loading state to false if no procedures
        return;
      }

      try {
        const companiesData = await Promise.all(
          proceduresWithAffiliateId.map(async (proc) => {
            try {
              const response = await api.get(`api/affiliatecompany/show/${proc.affiliate_company_id}`);
              return {
                procedureId: proc.id,
                companyName: response.data?.affiliate_company.name || 'Nome não encontrado'
              };
            } catch (error) {
              console.error(`Error fetching affiliate company for procedure ${proc.id}:`, error);
              return {
                procedureId: proc.id,
                companyName: 'Erro ao carregar'
              };
            }
          })
        );

        const companiesMap = {};
        companiesData.forEach(({ procedureId, companyName }) => {
          companiesMap[procedureId] = companyName;
        });

        setAffiliateCompanies(companiesMap);
      } catch (error) {
        console.error('Error processing affiliate companies:', error);
      } finally {
        setLoading(false); // Ensure loading state is set to false after processing
      }
    };

    fetchAffiliateCompanies();
  }, []);

  // useEffect(() => {
  //   const fetchAffiliateCompanies = async () => {
  //     const proceduresWithAffiliateId = patient.pending.filter(proc => proc.affiliate_company_id);

  //     if (proceduresWithAffiliateId.length === 0) return;

  //     try {
  //       const companiesData = await Promise.all(
  //         proceduresWithAffiliateId.map(async (proc) => {
  //           try {
  //             const response = await api.get(`api/affiliatecompany/show/${proc.affiliate_company_id}`);
  //             return {
  //               procedureId: proc.id,
  //               companyName: response.data?.affiliate_company.name || 'Nome não encontrado'
  //             };
  //           } catch (error) {
  //             console.error(`Error fetching affiliate company for procedure ${proc.id}:`, error);
  //             return {
  //               procedureId: proc.id,
  //               companyName: 'Erro ao carregar'
  //             };
  //           }
  //         })
  //       );

  //       const companiesMap = {};
  //       companiesData.forEach(({ procedureId, companyName }) => {
  //         companiesMap[procedureId] = companyName;
  //       });

  //       setAffiliateCompanies(companiesMap);
  //     } catch (error) {
  //       console.error('Error processing affiliate companies:', error);
  //     }
  //   };

  //   fetchAffiliateCompanies();
  // }, []);

  return (
    <div className="modal-overlay">
      <div className="procedures-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>Procedimentos – {patient.name}</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faX} />
          </button>
        </div>

        {/* Busca e Filtros */}
        <div className="modal-search">
          <div className="search-input">
          <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Pesquisar procedimento"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {selectedSector !== "all" && (
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={
                  filteredProcedures.length > 0 &&
                  filteredProcedures.every((p) =>
                    selectedProcedures.some((sp) => sp.id === p.id)
                  )
                }
                onChange={toggleSelectAll}
              />
              <span>Selecionar todos</span>
            </label>
          )}

          <div className="sector-filter">
            <label htmlFor="sector-select">Filtrar por setor:</label>
            <select
              id="sector-select"
              value={selectedSector}
              onChange={(e) => {
                setSelectedSector(e.target.value);
                setSelectedProcedures([]);
                setPayments({});
              }}>
              <option value="all">Todos</option>
              {sectors.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Ações em lote */}
        {selectedSector !== "all" && (
          <div className="bulk-payment-bar">
            <span>
              Aplicar ao setor <b>{selectedSector}</b>:
            </span>
            <button
              onClick={() =>
                applyPaymentToSector({ type: "private", insurance: "" })
              }>
              Particular
            </button>
            <select
              defaultValue=""
              onChange={(e) =>
                e.target.value &&
                applyPaymentToSector({
                  type: "insurance",
                  insurance: e.target.value,
                })
              }>
              <option value="" disabled>
                Convênio...
              </option>
              {insurances.map((ins) => (
                <option key={ins} value={ins}>
                  {ins}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Mensagem de erro */}
        {errorMessage && (
          <div className="error-message">
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Lista de procedimentos */}
        <div className="procedures-list">
          {Array.isArray(filteredProcedures) &&
            filteredProcedures.map((proc) => {
              const selected = selectedProcedures.some((p) => p.id === proc.id);
              const payInfo = payments[proc.id] ?? {
                type: "private",
                insurance: "",
              };

              return (
                <div
                  key={proc.id}
                  className={`procedure-item ${selected ? "selected" : ""}`}
                  onClick={() => handleProcedureSelect(proc)}>
                  <div className="procedure-checkbox">
                    <input type="checkbox" checked={selected} readOnly />
                  </div>

                  <div className="d-flex justify-content-between w-100">
                    <div className="procedure-details">
                      <h4>{proc.name}</h4>
                      <small>
                        código: {proc.cbhpm_code} &nbsp;|&nbsp; setor:{" "}
                        {proc.setor_name}
                      </small>

                      <div>
                        <div
                          className="payment-choice"
                          onClick={(e) => e.stopPropagation()}>
                          <label>
                            <input
                              type="radio"
                              name={`pay-${proc.id}`}
                              checked={payInfo.type === "private"}
                              onChange={() =>
                                updatePayment(proc.id, {
                                  type: "private",
                                  insurance: "",
                                })
                              }
                            />
                            Particular
                          </label>

                          <label>
                            <input
                              type="radio"
                              name={`pay-${proc.id}`}
                              checked={payInfo.type === "insurance"}
                              onChange={() =>
                                updatePayment(proc.id, {
                                  type: "insurance",
                                  insurance: insurances[0],
                                })
                              }
                            />
                            Convênio
                          </label>

                          {payInfo.type === "insurance" && (
                            <select
                              value={payInfo.insurance}
                              onChange={(e) =>
                                updatePayment(proc.id, {
                                  ...payInfo,
                                  insurance: e.target.value,
                                })
                              }>
                              {insurances.map((ins) => (
                                <option key={ins} value={ins}>
                                  {ins}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                      </div>
                    </div>

                    {
                      (proc.scheduling_id_secondary || proc.affiliate_company_id || affiliateCompanies[proc.id]) && (
                        <div style={{ width: '275px' }}>
                          {proc.scheduling_id_secondary && (
                            <div className="text-white py-2 px-4 rounded-pill mb-3 text-center" style={{ background: '#D3D3D3' }}>
                              <FontAwesomeIcon icon={faTag} className="me-2" />
                              Secundário
                            </div>
                          )}

                          <div>
                            {loading ?
                              <div>
                                Carregando ...
                              </div>
                              : proc.affiliate_company_id && affiliateCompanies[proc.id] && (
                                <div className="text-white py-2 px-4 rounded-pill mt-3 text-center display-flex align-items-center" style={{ background: '#D3D3D3' }}>
                                  <FontAwesomeIcon icon={faBuilding} className="me-2" />
                                  {affiliateCompanies[proc.id]}
                                </div>
                              )}
                          </div>
                        </div>
                      ) 
                    }
                  </div>

                </div>
              );
            })}

          {filteredProcedures.length === 0 && (
            <p className="no-procedures">Nenhum procedimento encontrado</p>
          )}
        </div>

        {/* Rodapé */}
        <div className="modal-footer">
          {selectedProcedures.length > 0 && (
            <div className="available-doctors">
              <FontAwesomeIcon icon={faUsers} />
              <span>
                {loadingDoctors
                  ? "Carregando médicos..."
                  : availableDoctorsCount > 0
                    ? `${availableDoctorsCount} médico(s) disponível(is)`
                    : "Nenhum médico disponível"}
              </span>
            </div>
          )}

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="schedule-button"
              onClick={handleProceed}
              disabled={
                selectedProcedures.length === 0 ||
                errorMessage ||
                availableDoctorsCount === 0
              }>
              Prosseguir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProceduresModal;
